import { doc, getDoc, collection, getDocs, query, where, orderBy, limit, startAfter, Timestamp, type QueryDocumentSnapshot, type DocumentData } from 'firebase/firestore';
import { db } from '../firebase';
import type { ObsessLessUser } from '../types/commonTypes';

export class UserService {
  private static instance: UserService;

  static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  private convertTimestampsToDate(data: Record<string, unknown>): Record<string, unknown> {
    const converted: Record<string, unknown> = { ...data };

    // Convert top-level timestamps
    Object.keys(converted).forEach((key) => {
      if (converted[key] instanceof Timestamp) {
        converted[key] = (converted[key] as Timestamp).toDate();
      }
      // Handle nested objects (like questJourney)
      else if (converted[key] && typeof converted[key] === 'object' && !Array.isArray(converted[key])) {
        const nested = converted[key] as Record<string, unknown>;
        Object.keys(nested).forEach((nestedKey) => {
          if (nested[nestedKey] instanceof Timestamp) {
            nested[nestedKey] = (nested[nestedKey] as Timestamp).toDate();
          }
        });
      }
    });

    return converted;
  }

  async getUserByUserId(userId: string): Promise<ObsessLessUser | null> {
    const userRef = doc(db, 'user_info', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      return null;
    }
    const userData = userDoc.data();
    if (!userData) {
      return null;
    }

    const convertedData = this.convertTimestampsToDate(userData);

    return {
      uid: userId,
      ...convertedData,
    } as ObsessLessUser;
  }

  async searchUsersByEmail(emailQuery: string): Promise<ObsessLessUser[]> {
    if (!emailQuery.trim()) {
      return [];
    }

    const normalizedQuery = emailQuery.toLowerCase().trim();

    try {
      // Since Firestore doesn't support case-insensitive or partial text search natively,
      // we'll use a range query and filter client-side
      const usersRef = collection(db, 'user_info');
      const q = query(usersRef, where('email', '>=', normalizedQuery), where('email', '<=', normalizedQuery + '\uf8ff'), orderBy('email'));

      const querySnapshot = await getDocs(q);
      const users: ObsessLessUser[] = [];

      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData) {
          const convertedData = this.convertTimestampsToDate(userData);
          users.push({
            uid: doc.id,
            ...convertedData,
          } as ObsessLessUser);
        }
      });

      // Additional client-side filtering for case-insensitive partial matching
      return users.filter((user) => user.email?.toLowerCase().includes(normalizedQuery));
    } catch (error) {
      console.error('Error searching users by email:', error);
      return [];
    }
  }

  async searchUsers(searchQuery: string): Promise<ObsessLessUser[]> {
    if (!searchQuery.trim()) {
      return [];
    }

    // First try to get user by exact ID match
    const userById = await this.getUserByUserId(searchQuery.trim());
    if (userById) {
      return [userById];
    }

    // If no user found by ID, search by email
    return this.searchUsersByEmail(searchQuery);
  }

  async getTopJourneyUsers(
    limitCount: number = 20,
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
  ): Promise<{ users: ObsessLessUser[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
    try {
      const usersRef = collection(db, 'user_info');
      const constraints = [
        orderBy('questJourney.currentDay', 'desc'),
        ...(lastDoc ? [startAfter(lastDoc)] : []),
        limit(limitCount),
      ];
      const q = query(usersRef, ...constraints);

      const querySnapshot = await getDocs(q);
      const users: ObsessLessUser[] = [];
      let lastVisible: QueryDocumentSnapshot<DocumentData> | null = null;

      querySnapshot.forEach((docSnap) => {
        const userData = docSnap.data();
        if (userData && userData.questJourney) {
          const convertedData = this.convertTimestampsToDate(userData);
          users.push({
            uid: docSnap.id,
            ...convertedData,
          } as ObsessLessUser);
          lastVisible = docSnap;
        }
      });

      return {
        users,
        lastDoc: users.length < limitCount ? null : lastVisible,
      };
    } catch (error) {
      console.error('Error fetching top journey users:', error);
      throw error;
    }
  }

  async getUsersCreatedInLast12Months(): Promise<{ month: string; count: number }[]> {
    try {
      // Calculate 12 months ago from today
      const now = new Date();
      const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      const startTimestamp = Timestamp.fromDate(twelveMonthsAgo);

      const usersRef = collection(db, 'user_info');
      const q = query(
        usersRef,
        where('createdAt', '>=', startTimestamp),
        orderBy('createdAt', 'asc')
      );

      const querySnapshot = await getDocs(q);

      // Group by month
      const monthCounts: Record<string, number> = {};

      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData?.createdAt) {
          const createdAt = userData.createdAt instanceof Timestamp
            ? userData.createdAt.toDate()
            : new Date(userData.createdAt);

          const year = createdAt.getFullYear();
          const month = (createdAt.getMonth() + 1).toString().padStart(2, '0');
          const monthKey = `${year}-${month}`;

          monthCounts[monthKey] = (monthCounts[monthKey] || 0) + 1;
        }
      });

      // Build array for all 12 months (including months with 0 users)
      const result: { month: string; count: number }[] = [];
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const monthKey = `${year}-${month}`;

        result.push({
          month: monthKey,
          count: monthCounts[monthKey] || 0,
        });
      }

      return result;
    } catch (error) {
      console.error('Error fetching users created in last 12 months:', error);
      throw error;
    }
  }
}

export const userService = UserService.getInstance();
